<template>
  <nav ref="navigationRef" :class="containerClasses">
    <div
      :class="[
        'flex',
        !isVertical ? 'flex-row items-center' : 'flex-1 flex-col items-center',
        isMobile
          ? 'mx-auto h-full w-full max-w-(--breakpoint-sm) items-center justify-around'
          : 'overflow-hidden'
      ]"
    >
      <template v-if="isMobile">
        <MobileNavigationPane
          :items="items"
          :selectedItem="selectedItemId"
          :showSettings="showSettings"
          :showAuth="showAuth"
          :showCoachAI="showCoachAI"
          :isLoggedIn="isLoggedIn"
          :profile="profile"
          @setSelectedItem="selectItem"
        />
      </template>

      <template v-else>
        <NavigationPaneBrand :expanded="expanded" />
        <NavigationPaneProfile :profile="profile" :expanded="expanded" :isLoggedIn="isLoggedIn" />

        <div
          :class="[
            'flex w-full flex-1 justify-between overflow-hidden',
            isVertical ? 'flex-col' : 'flex-row'
          ]"
        >
          <NavigationWrapper
            :items="items"
            :expanded="expanded"
            :position="effectivePosition"
            :selectedItem="selectedItemId"
            :showDashboard="true"
            :showCoachAI="showCoachAI"
            @selectItem="selectItem"
          />

          <NavigationPaneUtils
            :canExpand="canExpand"
            :position="effectivePosition"
            :expanded="expanded"
            :showSettings="showSettings"
            :showAuth="showAuth"
            :isLoggedIn="isLoggedIn"
            @toggle="toggleExpanded"
          />
        </div>
      </template>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onUnmounted } from 'vue'
import { containerClassBuilder } from '../utils/navigationClasses'
import { navigationStore } from '../store/navigationStore'
import NavigationPaneBrand from './NavigationPaneBrand.vue'
import NavigationPaneProfile from './NavigationPaneProfile.vue'
import NavigationWrapper from './NavigationWrapper.vue'
import NavigationPaneUtils from './NavigationPaneUtils.vue'
import MobileNavigationPane from './MobileNavigationPane.vue'
import { type NavItem } from '../types/NavigationPane'
import type { Profile } from '../NavigationPaneComponent.vue'

export default defineComponent({
  name: 'InnerNavigationPane',
  components: {
    NavigationPaneBrand,
    NavigationPaneProfile,
    NavigationWrapper,
    NavigationPaneUtils,
    MobileNavigationPane
  },
  props: {
    items: {
      type: Array as () => NavItem[],
      required: true
    },
    profile: {
      type: Object as () => Profile,
      required: true
    }
  },
  setup(props) {
    const store = navigationStore
    const navigationRef = ref<HTMLElement | null>(null)

    const containerClasses = computed(() =>
      containerClassBuilder(
        store.state.effectivePosition,
        store.state.expanded,
        store.state.isMobile
      )
    )

    const isVertical = computed(
      () => store.state.effectivePosition === 'left' || store.state.effectivePosition === 'right'
    )

    const selectItem = (id: string) => {
      store.selectItem(id, props.items)
    }

    // Click outside handler to close navigation
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle click outside if menu is expanded and not on mobile
      if (store.state.expanded && !store.state.isMobile && navigationRef.value) {
        const target = event.target as Node
        // Check if the click was outside the navigation element
        if (!navigationRef.value.contains(target)) {
          store.setExpanded(false)
        }
      }
    }

    onMounted(() => {
      // Add click outside listener
      document.addEventListener('mousedown', handleClickOutside)
    })

    onUnmounted(() => {
      // Clean up event listener
      document.removeEventListener('mousedown', handleClickOutside)
    })

    // Expose store properties for use in template
    return {
      selectItem,
      containerClasses,
      isVertical,
      navigationRef,
      // Store state
      isMobile: computed(() => store.state.isMobile),
      effectivePosition: computed(() => store.state.effectivePosition),
      expanded: computed(() => store.state.expanded),
      selectedItemId: computed(() => store.state.selectedItemId),
      toggleExpanded: store.toggleExpanded,
      canExpand: computed(() => store.state.canExpand),
      showSettings: computed(() => store.state.showSettings),
      showAuth: computed(() => store.state.showAuth),
      isLoggedIn: computed(() => store.state.isLoggedIn),
      showCoachAI: computed(() => store.state.showCoachAI)
    }
  }
})
</script>
